import React from 'react';
import PropTypes from 'prop-types';
import DropdownA from 'components/Dropdown/DropdownA';
import ResultTableA from 'components/ResultTable/ResultTableA';
import Button from 'components/Button';
import classes from './DesigneeSearchA.module.scss';

const DesigneeSearchA = (props) => {
  const {
    results,
    error,
    firstName,
    lastName,
    city,
    onChangeFirstName,
    onChangeLastName,
    onChangeCity,
    onSubmit,
    onClear,
    stateList,
    designationList,
    selectedState,
    selectedDesignation,
    onChangeSelectedState,
    onChangeSelectedDesignation,
    zipCode,
    onChangeZipCode,
    activePage,
    onPageChange,
    onSortChange,
    sortConfig,
    loading,
  } = props;

  const getLabelText = (item = {}) => {
    const { name = '', acronym = '', acronymMark = '' } = item;
    const labelText = `${name}${acronym ? ` or ${acronym}${acronymMark || ''}` : ''}`;
    return labelText;
  };

  const renderOption = (item = {}, className) => {
    const labelTxt = getLabelText(item);
    return (
      <span aria-label={labelTxt} className={className}>
        { item.name && (
        <span
          className={classes.dropdownTitle}
          dangerouslySetInnerHTML={{ __html: labelTxt }}
        />
        )}
      </span>
    );
  };

  const listDisplay = (item) => renderOption(item, classes.optionWrapper);
  const singleValue = (item) => renderOption(item, classes.singleValueWrapper);

  const expandItemsInList = (list) => list.map((item) => ({
    ...item,
    label: getLabelText(item),
    renderSingleValue: singleValue(item),
    renderOption: listDisplay(item),
  }));

  const designationDropDownList = expandItemsInList(designationList);
  const stateDropDownList = expandItemsInList(stateList);

  const expandSelectedObject = (selectedObject) => {
    if (selectedObject) {
      return {
        ...selectedObject,
        label: selectedObject.name,
        renderSingleValue: singleValue(selectedObject),
        renderOption: listDisplay(selectedObject),
      };
    }
    return null;
  };

  const stateDropdownValue = expandSelectedObject(selectedState);
  const designationDropdownValue = expandSelectedObject(selectedDesignation);

  const renderErrorMessage = (text) => (
    <div className={classes.errorMessage}>
      {text}
    </div>
  );

  const customStylesForDropdown = {
    container: (base) => ({ ...base, width: '100%', border: 'none', minHeight: '40px' }),
    control: (base) => ({
      ...base,
      border: '1px solid #222121',
      borderRadius: '4px',
      height: '100%',
      minHeight: '40px',
    }),
    singleValue: (base) => (
      {
        ...base,
        position: 'relative',
        transform: 'none',
        whiteSpace: 'normal',
        width: 'calc(100% - 21px)',
      }
    ),
    placeholder: (base) => (
      {
        ...base,
        color: '#222121',
      }
    ),
  };

  const renderFormInput = (htmlFor, labetText, inputValue, onChange) => (
    <div className={classes.fieldWrapper}>
      <label htmlFor={htmlFor}>
        <span className={classes.label}>{labetText}</span>
        <input
          id={htmlFor}
          className={classes.input}
          type='search'
          value={inputValue}
          onChange={(evt) => onChange(evt.target.value)}
        />
      </label>
    </div>
  );

  const renderFormDropdown = (spanText, type, optionList, value, onChange) => (
    <div className={classes.fieldWrapper}>
      <span className={classes.label}>{spanText}</span>
      <div className={classes.dropdownWrapper}>
        <DropdownA
          type={type}
          options={optionList}
          value={value}
          defaultValue={value}
          onChange={onChange}
          renderOption='renderOption'
          renderSingleValue='renderSingleValue'
          isSearchable
          isClearable
          placeholder='Select an option...'
          customStyles={customStylesForDropdown}
        />
      </div>
    </div>
  );

  return (
    <>
      <form onSubmit={onSubmit} className={classes.container}>
        <div className={classes.fieldsContainer}>
          {renderFormInput('fname', 'First Name', firstName, onChangeFirstName)}
          {renderFormInput('lname', 'Last Name', lastName, onChangeLastName)}
          {renderFormInput('city', 'City Name', city, onChangeCity)}
          {renderFormDropdown(
            'Select State',
            'StateList',
            stateDropDownList,
            stateDropdownValue,
            onChangeSelectedState,
          )}
          {renderFormInput('zCode', 'Zip Code', zipCode, onChangeZipCode)}
          {renderFormDropdown(
            'Select a Designation',
            'DesignationList',
            designationDropDownList,
            designationDropdownValue,
            onChangeSelectedDesignation,
          )}
        </div>
        {error && renderErrorMessage(error)}
        <div className={classes.buttonsContainer}>
          <Button
            actionType='submit'
            subType='ButtonA'
            title='Search'
            rightIcon='far fa-chevron-right'
            className={classes.button}
          />
          <button aria-label='Clear' onClick={onClear} type='button' className={classes.clearButton}>
            Clear
          </button>
        </div>
      </form>
      <ResultTableA
        results={results}
        activePage={activePage}
        onPageChange={onPageChange}
        sortConfig={sortConfig}
        onSortChange={onSortChange}
        loading={loading}
      />
    </>
  );
};

DesigneeSearchA.propTypes = {
  /**
   * Dictionary with the data for pagiantion and result set
  */
  results: PropTypes.shape({
    itemsPerPage: PropTypes.number,
    totalRecords: PropTypes.number,
    items: PropTypes.array,
  }),
  /**
   * Error Message to display when performing the search
  */
  error: PropTypes.string,
  /**
   * Value of the form input of: First name
  */
  firstName: PropTypes.string,
  /**
   * Value of the form input of: Last name
  */
  lastName: PropTypes.string,
  /**
   * Value of the form input of: City Name
  */
  city: PropTypes.string,
  /**
   * Options for the States dropdown
  */
  stateList: PropTypes.array,
  /**
   * Selected Option in the State dropdown
  */
  selectedState: PropTypes.object,
  /**
   * Value of the form input of: Zip Code
  */
  zipCode: PropTypes.string,
  /**
   * Options for the Designation dropdown
  */
  designationList: PropTypes.array,
  /**
   * Selected Option in the Designation dropdown
  */
  selectedDesignation: PropTypes.object,
  /**
   * Active Page on the Pagination
  */
  activePage: PropTypes.number,
  /**
   * Indicates which option is the result set using for sorting and its order (asc or desc)
  */
  sortConfig: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.number,
  }),
  /**
   * Callback to change the First Name
  */
  onChangeFirstName: PropTypes.func,
  /**
   * Callback to change the Last Name
  */
  onChangeLastName: PropTypes.func,
  /**
   * Callback to change the City Name
  */
  onChangeCity: PropTypes.func,
  /**
   * Callback to perform the search
  */
  onSubmit: PropTypes.func,
  /**
   * Callback to clear the search form
  */
  onClear: PropTypes.func,
  /**
   * Callback to change the State
  */
  onChangeSelectedState: PropTypes.func,
  /**
   * Callback to change the Designation
  */
  onChangeSelectedDesignation: PropTypes.func,
  /**
   * Callback to change the Zip Code
  */
  onChangeZipCode: PropTypes.func,
  /**
   * Callback to change the Page Number
  */
  onPageChange: PropTypes.func,
  /**
   * Callback to change the Sorting field and order
  */
  onSortChange: PropTypes.func,
  /**
   * Boolean to tell if the search is loading
  */
  loading: PropTypes.bool,
};

export default DesigneeSearchA;
